import { Box, List, ListItem, Stack, Tooltip, Typography } from '@mui/material'
import { FeatureFlagIdCopilotInsights, FeatureFlagIdGrantDestinations } from 'c1gen/features'
import { C1IconButton } from 'c1ui/components/C1IconButton'
import { C1EmptyIndicator } from 'components/core/empty-indicator/C1EmptyIndicator'
import { C1Icon } from 'components/core/icons/C1Icon'
import { UsageLabel, StatusLabel, ReviewRiskLabel, Analysis, EmploymentStatusLabel } from 'components/core/insights'
import { AccessConflictLabel } from 'components/core/insights/insight-kinds/analysis/AccessConflict'
import { RecommendationInsightsProps } from 'components/core/insights/RecommendationInsights'
import { InsightRecommendationStyles } from 'components/core/insights/styles'
import { HierarchyDrawer } from 'components/core/task/insights/HierarchyDrawer'
import { useFeatureChecker } from 'components/no-ui/capability'
import { useDrawer } from 'hooks/useDrawer'
import { TaskValue } from 'lib/actions/task'
import { Flag, GitFork, TriangleAlert } from 'lucide-react'
import { AccessConflictOutcome } from 'pbts/c1/api/analysis/v1/analysis'
import { InsightType } from 'pbts/c1/api/task/v1/insights'
import { Any } from 'pbts/google/protobuf/any'
import React, { FC, useCallback } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'

export const ReviewInsightsTooltipContent: FC<RecommendationInsightsProps> = ({ task }) => {
    const hasCopilotInsights = useFeatureChecker(FeatureFlagIdCopilotInsights)
    const insights = task.insights
    if (!hasCopilotInsights || !insights) {
        return null
    }

    return (
        <Box sx={InsightRecommendationStyles.recommendations}>
            <Typography variant="h6">
                <FormattedMessage
                    id="copilot_insights_tooltip_title"
                    defaultMessage="Flagged insights"
                />
            </Typography>
            <List sx={InsightRecommendationStyles.list}>
                {insights.map((insight, i) => {
                    if (insight.kind.oneofKind === 'usage' && insight.type === InsightType.NEGATIVE) {
                        return (
                            <ListItem key={`rec_item_usage_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <UsageLabel usage={insight.kind.usage} app={task.app} appUser={task.appUser} />
                            </ListItem>
                        )
                    }
                    if (insight.kind.oneofKind === 'userStatus' && insight.type === InsightType.NEGATIVE) {
                        return (
                            <ListItem key={`rec_item_user_status_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <StatusLabel userStatus={insight.kind.userStatus.userStatus} user={task.identityUser || task.user} />
                            </ListItem>
                        )
                    }
                    if (insight.kind.oneofKind === 'employmentStatus' && insight.type === InsightType.NEGATIVE) {
                        return (
                            <ListItem key={`rec_item_employment_status_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <EmploymentStatusLabel
                                    employmentStatus={insight.kind.employmentStatus.employmentStatus}
                                    employmentStatusSources={insight.kind.employmentStatus.employmentStatusSources}
                                    user={task.identityUser || task.user}
                                />
                            </ListItem>
                        )
                    }
                    if (insight.kind.oneofKind === 'risk' && insight.type === InsightType.NEGATIVE) {
                        return (
                            <ListItem key={`rec_item_risk_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <ReviewRiskLabel risk={insight.kind.risk} task={task} />
                            </ListItem>
                        )
                    }
                    if (insight.kind.oneofKind === 'analysis' && insight.type === InsightType.NEGATIVE) {
                        return (
                            <ListItem key={`rec_item_analysis_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <Analysis
                                    analysis={insight.kind.analysis}
                                    insightType={InsightType.NEGATIVE}
                                    showIcon={false}
                                    task={task}
                                />
                            </ListItem>
                        )
                    }
                    return null
                })}
            </List>
        </Box>
    )
}

export const ReviewInsightsAlertsTooltipContent: FC<RecommendationInsightsProps> = ({ task }) => {
    const hasCopilotInsights = useFeatureChecker(FeatureFlagIdCopilotInsights)
    const insights = task.insights
    if (!hasCopilotInsights || !insights) {
        return null
    }

    return (
        <Box sx={InsightRecommendationStyles.recommendations}>
            <Typography variant="h6">
                <FormattedMessage
                    id="copilot_insights_tooltip_title"
                    defaultMessage="Alerts"
                />
            </Typography>
            <List sx={InsightRecommendationStyles.list}>
                {insights.map((insight, i) => {
                    if (insight.kind.oneofKind === 'analysis'
                        && insight.type === InsightType.ALERT
                        && Any.typeUrlToName(insight.kind.analysis.outcome?.outcome?.typeUrl || '') === AccessConflictOutcome.typeName
                    ) {
                        return (
                            <ListItem key={`rec_item_employment_status_${i}`} sx={InsightRecommendationStyles.listItem}>
                                <AccessConflictLabel task={task} />
                            </ListItem>
                        )
                    }
                    return null
                })}
            </List>
        </Box>
    )
}

export const ReviewInsights = ({ row }: { row: TaskValue }): React.ReactNode => {
    const hasGrantDestinations = useFeatureChecker(FeatureFlagIdGrantDestinations)
    const hasCopilotInsights = useFeatureChecker(FeatureFlagIdCopilotInsights)
    const [openDrawer] = useDrawer()
    const intl = useIntl()

    const handleHierarchyClick = useCallback((): void => {
        openDrawer(<HierarchyDrawer task={row} />, { variant: 'sm' })
    }, [openDrawer, row])

    const numFlags = row.insights?.reduce(
        (acc, insight): number => {
            if (insight.type === InsightType.NEGATIVE) {
                return acc + 1
            }
            return acc
        },
        0,
    )

    const numAlerts = row.insights?.reduce(
        (acc, insight): number => {
            if (insight.type === InsightType.ALERT) {
                return acc + 1
            }
            return acc
        },
        0,
    )

    if (numFlags === 0 && numAlerts === 0 && !hasGrantDestinations) {
        return <Box>
            <C1EmptyIndicator />
        </Box>
    }

    return (
        <Stack direction="row" alignItems="center" spacing={1}>
            {numFlags !== 0 && hasCopilotInsights && (
                <Tooltip title={<ReviewInsightsTooltipContent task={row} />}>
                    <Box position="relative" left="-0.45rem">
                        <C1IconButton aria-label={intl.formatMessage({ id: 'review_insights_show_flagged', defaultMessage: 'Show flagged insights' })}>
                            <C1Icon icon={Flag} sx={{ color: 'icons.gold' }} />
                        </C1IconButton>
                    </Box>
                </Tooltip>
            )}

            {numAlerts !== 0 && hasCopilotInsights && (
                <Tooltip title={<ReviewInsightsAlertsTooltipContent task={row} />}>
                    <Box position="relative" left="-0.45rem">
                        <C1IconButton aria-label={intl.formatMessage({ id: 'review_insights_show_alerts', defaultMessage: 'Show alerts' })}>
                            <C1Icon icon={TriangleAlert} sx={{ color: 'icons.vermillion' }} />
                        </C1IconButton>
                    </Box>
                </Tooltip>
            )}

            {hasGrantDestinations && (
                <Tooltip title="View hierarchy">
                    <Box position="relative" left="-0.45rem">
                        <C1IconButton onClick={handleHierarchyClick} aria-label={intl.formatMessage({ id: 'review_insights_view_hierarchy', defaultMessage: 'View hierarchy' })}>
                            <C1Icon icon={GitFork} sx={{ color: 'text.secondary' }} />
                        </C1IconButton>
                    </Box>
                </Tooltip>
            )}
        </Stack>
    )
}
